import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultShapeFactors = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const {
    canUserSeeCalculationSteps,
    phips,
    effectiveFndWidth,
    effectiveFndLength,
    isInclinedLoading,
    isPhiZero,
    nc,
    nq,
    icb,
    icl,
    iqb,
    iql,
    iGammab,
    iGammal,
    sc,
    scb,
    scl,
    sq,
    sqb,
    sql,
    sGamma,
    sGammab,
    sGammal,
    sGammabInitial,
    sGammalInitial,
    issGammabOver06,
    issGammalOver06,
    sGammaInitial,
    issGammaOver06,
  } = result.output;

  const case1 = isInclinedLoading && isPhiZero;
  const case2 = isInclinedLoading && !isPhiZero;
  const case3 = !isInclinedLoading && isPhiZero;
  const case4 = !isInclinedLoading && !isPhiZero;
  const caseLegendItems = [
    { label: 'CASE 1', description: 'Inclined loading with \u03c6 = 0', isActive: case1 },
    { label: 'CASE 2', description: 'Inclined loading with \u03c6 \u2260 0', isActive: case2 },
    { label: 'CASE 3', description: 'No inclined loading with \u03c6 = 0', isActive: case3 },
    { label: 'CASE 4', description: 'No inclined loading with \u03c6 \u2260 0', isActive: case4 },
  ];
  const [showCase13ScDetails, setShowCase13ScDetails] = useState(false);
  const [showCase2SqDetails, setShowCase2SqDetails] = useState(false);
  const [showCase2ScDetails, setShowCase2ScDetails] = useState(false);
  const [showCase2SgammaDetails, setShowCase2SgammaDetails] = useState(false);
  const [showCase4ScDetails, setShowCase4ScDetails] = useState(false);
  const [showCase4SqDetails, setShowCase4SqDetails] = useState(false);
  const [showCase4SgammaDetails, setShowCase4SgammaDetails] = useState(false);

  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const successAlertSx = {
    backgroundColor: 'rgba(46,125,50,0.04)',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      alignSelf: 'flex-start',
      mt: 0.25,
      minWidth: 24,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      mt: 0.25,
      width: 32,
      justifyContent: 'center',
      p: 0,
      ml: 1,
    },
    '& .MuiAlert-message': { width: '100%' },
  };

  const renderAlertAction = (isExpanded, setExpanded) =>
    canUserSeeCalculationSteps ? (
      <IconButton size="small" color="inherit" onClick={() => setExpanded((v) => !v)} sx={{ width: 32, height: 32 }}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    ) : (
      <Tooltip
        title={
          <span>
            Upgrade to unlock calculation details.{' '}
            <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
              See plans
            </Link>
          </span>
        }
        leaveDelay={500}
        arrow
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'text.disabled',
            cursor: 'not-allowed',
            mr: 1,
            mt: 0.5,
          }}
        />
      </Tooltip>
    );

  const Case1Case3Sc = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s'<sub>c</sub>
        </h4>
        <p>
          &phi; = 0 &rarr; Only s'<sub>c</sub> should be calculated.
        </p>
        <p>
          s'<sub>c</sub> = 0.2 (B' / L')
        </p>
        <p>
          s'<sub>c</sub> = 0.2 ({round(effectiveFndWidth, 2)}) / ({round(effectiveFndLength, 2)})
        </p>
        <p>
          s'<sub>c</sub> = {round(sc, 2)}
        </p>
      </div>
    );
  };

  const Case2Sq = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>q</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          s<sub>q, B</sub> and s<sub>q, L</sub> should be calculated.
        </p>
        <br />
        <p>
          s<sub>q, B</sub> = 1 + (B' / L') (i<sub>q, B</sub>) sin&phi;
        </p>
        <p>
          s<sub>q, B</sub> = 1 + ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) ({round(iqb, 2)}) (sin
          {round(phips, 2)});
        </p>
        <p>
          s<sub>q, B</sub>= {round(sqb, 2)}
        </p>
        <br />
        <p>
          s<sub>q, L</sub> = 1 + (L' / B') (i<sub>q, L</sub>) sin&phi;
        </p>
        <p>
          s<sub>q, L</sub> = 1 + ({round(effectiveFndLength, 2)} / {round(effectiveFndWidth, 2)}) ({round(iql, 2)}) (sin
          {round(phips, 2)});
        </p>
        <p>
          s<sub>q, L</sub>= {round(sql, 2)}
        </p>
      </div>
    );
  };
  const Case2Sc = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>c</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          s<sub>c, B</sub> and s<sub>c, L</sub> should be calculated.
        </p>
        <br />
        <p>
          s<sub>c, B</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (B' / L') (i<sub>c, B</sub>)
        </p>
        <p>
          s<sub>c, B</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}
          ) ({round(icb, 2)})
        </p>
        <p>
          s<sub>c, B</sub> = {round(scb, 2)}
        </p>
        <br />
        <p>
          s<sub>c, L</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (L' / B') (i<sub>c, L</sub>)
        </p>
        <p>
          s<sub>c, L</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndLength, 2)} / {round(effectiveFndWidth, 2)}
          ) ({round(icl, 2)})
        </p>
        <p>
          s<sub>c, L</sub> = {round(scl, 2)}
        </p>
      </div>
    );
  };
  const Case2Sgamma = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>&gamma;</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          s<sub>&gamma;, B</sub> and s<sub>&gamma;, L</sub> should be calculated.
        </p>
        <br />
        <p>
          s<sub>&gamma;, B</sub> = 1 - 0.4 (B' / L') (i<sub>&gamma;, B</sub> / i<sub>&gamma;, L</sub>)
        </p>
        <p>
          s<sub>&gamma;, B</sub> = 1 - 0.4 ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) ({round(iGammab, 2)} /{' '}
          {round(iGammal, 2)})
        </p>
        <p>
          s<sub>&gamma;, B</sub> = {round(sGammabInitial, 2)}{' '}
          {issGammabOver06 ? (
            <span> &ge; 0.6 (OK)</span>
          ) : (
            <span>
              &lt;0.6 (NOT OK) Use s<sub>&gamma;, B</sub> = {round(sGammab, 2)}
            </span>
          )}
        </p>
        <br />
        <p>
          s<sub>&gamma;, L</sub> = 1 - 0.4 (L' / B') (i<sub>&gamma;, L</sub> / i<sub>&gamma;, B</sub>)
        </p>
        <p>
          s<sub>&gamma;, L</sub> = 1 - 0.4 ({round(effectiveFndLength, 2)} / {round(effectiveFndWidth, 2)}) ({round(iGammal, 2)} /{' '}
          {round(iGammab, 2)})
        </p>
        <p>
          s<sub>&gamma;, L</sub> = {round(sGammalInitial, 2)}{' '}
          {issGammalOver06 ? (
            <span> &ge; 0.6 (OK)</span>
          ) : (
            <span>
              &lt;0.6 (NOT OK) <br />
              Use s<sub>&gamma;, L</sub> = {round(sGammal, 2)}
            </span>
          )}
        </p>
      </div>
    );
  };

  const Case4Sc = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>c</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          s<sub>c</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (B' / L')
        </p>
        <p>
          s<sub>c</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)})
        </p>
        <p>
          s<sub>c</sub> = {round(sc, 2)}
        </p>
      </div>
    );
  };
  const Case4Sq = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>q</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          s<sub>q</sub> = 1 + (B' / L') sin&phi;
        </p>
        <p>
          s<sub>q</sub> = 1 + ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) (sin
          {round(phips, 2)});
        </p>
        <p>
          s<sub>q</sub> = {round(sq, 2)}
        </p>
      </div>
    );
  };
  const Case4Sgamma = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          s<sub>&gamma;</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          s<sub>&gamma;</sub> = 1 - 0.4 (B' / L')
        </p>
        <p>
          s<sub>&gamma;</sub> = 1 - 0.4 ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)})
        </p>
        <p>
          s<sub>&gamma;</sub> = {round(sGammaInitial, 2)}{' '}
          {issGammaOver06 ? (
            <span> &ge; 0.6 (OK)</span>
          ) : (
            <span>
              &lt;0.6 (NOT OK){' '}
              <p>
                Use s<sub>&gamma;, L</sub> = {round(sGamma, 2)}
              </p>
            </span>
          )}
        </p>
      </div>
    );
  };
  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Hansen Shape Factors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes Hansen shape factors (s<sub>c</sub>, s<sub>q</sub>, and s<sub>&gamma;</sub>) based on load
          inclination and friction-angle conditions. The 4 cases that can happen based on these conditions are listed below:
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Case Legend
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
            {caseLegendItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.7,
                  backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                }}
              >
                {renderCaseBadge(item.label, {
                  ml: 0,
                  minWidth: 64,
                  justifyContent: 'center',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  color: item.isActive ? 'success.dark' : 'text.disabled',
                  backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                  fontWeight: item.isActive ? 700 : 600,
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Case 1/3 s'c Factor */}
          {(case1 || case3) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase13ScDetails, setShowCase13ScDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factor (s'<sub>c</sub>) = {round(sc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase13ScDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    &phi; = 0 &rarr; Only s'<sub>c</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s'<sub>c</sub> = 0.2 (B' / L')
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s'<sub>c</sub> = 0.2 ({round(effectiveFndWidth, 2)}) / ({round(effectiveFndLength, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s'<sub>c</sub> = {round(sc, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 sq Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2SqDetails, setShowCase2SqDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factors &rarr; s<sub>q,B</sub> = {round(sqb, 2)}, s<sub>q,L</sub> = {round(sql, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2SqDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, B</sub> and s<sub>q, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, B</sub> = 1 + (B' / L') (i<sub>q, B</sub>) sin&phi;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, B</sub> = 1 + ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) ({round(iqb, 2)}) (sin
                    {round(phips, 2)});
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, B</sub>= {round(sqb, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, L</sub> = 1 + (L' / B') (i<sub>q, L</sub>) sin&phi;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q, L</sub> = 1 + ({round(effectiveFndLength, 2)} / {round(effectiveFndWidth, 2)}) ({round(iql, 2)}) (sin
                    {round(phips, 2)});
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>q, L</sub>= {round(sql, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 sc Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2ScDetails, setShowCase2ScDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factors &rarr; s<sub>c,B</sub> = {round(scb, 2)}, s<sub>c,L</sub> = {round(scl, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2ScDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, B</sub> and s<sub>c, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, B</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (B' / L') (i<sub>c, B</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, B</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndWidth, 2)} /{' '}
                    {round(effectiveFndLength, 2)}) ({round(icb, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, B</sub> = {round(scb, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, L</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (L' / B') (i<sub>c, L</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c, L</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndLength, 2)} /{' '}
                    {round(effectiveFndWidth, 2)}) ({round(icl, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>c, L</sub> = {round(scl, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 s_gamma Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2SgammaDetails, setShowCase2SgammaDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factors &rarr; s<sub>&gamma;,B</sub> = {round(sGammab, 2)}, s<sub>&gamma;,L</sub> = {round(sGammal, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2SgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, B</sub> and s<sub>&gamma;, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, B</sub> = 1 - 0.4 (B' / L') (i<sub>&gamma;, B</sub> / i<sub>&gamma;, L</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, B</sub> = 1 - 0.4 ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) (
                    {round(iGammab, 2)} / {round(iGammal, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, B</sub> = {round(sGammabInitial, 2)}{' '}
                    {issGammabOver06 ? (
                      <span>&ge; 0.6 (OK)</span>
                    ) : (
                      <span>
                        &lt;0.6 (NOT OK) Use s<sub>&gamma;, B</sub> = {round(sGammab, 2)}
                      </span>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, L</sub> = 1 - 0.4 (L' / B') (i<sub>&gamma;, L</sub> / i<sub>&gamma;, B</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;, L</sub> = 1 - 0.4 ({round(effectiveFndLength, 2)} / {round(effectiveFndWidth, 2)}) (
                    {round(iGammal, 2)} / {round(iGammab, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>&gamma;, L</sub> = {round(sGammalInitial, 2)}{' '}
                    {issGammalOver06 ? (
                      <span>&ge; 0.6 (OK)</span>
                    ) : (
                      <span>
                        &lt;0.6 (NOT OK) <br />
                        Use s<sub>&gamma;, L</sub> = {round(sGammal, 2)}
                      </span>
                    )}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 sc Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4ScDetails, setShowCase4ScDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factor (s<sub>c</sub>) = {round(sc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4ScDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c</sub> = 1 + (N<sub>q</sub> / N<sub>c</sub>) (B' / L')
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>c</sub> = 1 + ({round(nq, 2)} / {round(nc, 2)}) ({round(effectiveFndWidth, 2)} /{' '}
                    {round(effectiveFndLength, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>c</sub> = {round(sc, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 sq Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4SqDetails, setShowCase4SqDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factor (s<sub>q</sub>) = {round(sq, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4SqDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q</sub> = 1 + (B' / L') sin&phi;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>q</sub> = 1 + ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)}) (sin
                    {round(phips, 2)});
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>q</sub> = {round(sq, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 s_gamma Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4SgammaDetails, setShowCase4SgammaDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Shape factor (s<sub>&gamma;</sub>) = {round(sGamma, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4SgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;</sub> = 1 - 0.4 (B' / L')
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    s<sub>&gamma;</sub> = 1 - 0.4 ({round(effectiveFndWidth, 2)} / {round(effectiveFndLength, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    s<sub>&gamma;</sub> = {round(sGammaInitial, 2)}{' '}
                    {issGammaOver06 ? (
                      <span>&ge; 0.6 (OK)</span>
                    ) : (
                      <span>
                        &lt;0.6 (NOT OK)
                        <br />
                        Use s<sub>&gamma;, L</sub> = {round(sGamma, 2)}
                      </span>
                    )}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultShapeFactors;
